import React, { FC, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { batch } from 'react-redux';

import { setModal } from '../../../redux/slices/modalSlice';
import { useAppDispatch } from '../../../hooks';
import { ModalProps } from './types';

const Modal: FC<ModalProps> = ({
  children,
  size = `default`,
  isShow = false,
  title,
  className,
  isXButton = true,
  explanation,
  headerComponentExtended,
  footerComponent,
  onClose = () => {},
}) => {
  const dispatch = useAppDispatch();
  const modalRef = useRef<HTMLDivElement | null>(null);

  const modalSizeClassName = useMemo(
    () => ({ default: ``, sm: `modal-sm`, fit: `modal-fit` }[size]),
    [size],
  );

  const modalVisibleClassName = useMemo(
    () => (isShow ? `modal-show` : ``),
    [isShow],
  );

  const modalClassName = useMemo(
    () =>
      [`modal`, modalSizeClassName, modalVisibleClassName, className]
        .join(` `)
        .trim(),
    [className, modalSizeClassName, modalVisibleClassName],
  );

  const handleModalHide = useCallback(() => {
    batch(() => {
      dispatch(setModal(`Hidden`));
      onClose();
    });
  }, [dispatch, onClose]);

  return createPortal(
    <div ref={modalRef} className={modalClassName}>
      <div className="modal-overlay" onClick={handleModalHide} />
      <div className="modal-container">
        <div className="modal-header">
          {isXButton && (
            <button
              className="btn btn-clear float-right"
              onClick={handleModalHide}
            />
          )}

          {title && <div className="modal-title">{title}</div>}
          <p>{explanation}</p>
          {headerComponentExtended}
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-footer">{footerComponent}</div>
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
